import bgImg from '@/assets/img/curved-images/top_bg.svg';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { SpriteSvg } from '@/components/element/SpriteSvg';
import { SessionLayout } from '@/components/layout/SessionLayout';
import { useSignIn } from '@/hooks/api/0040_sign_in';
import { useGoogleAuth } from '@/hooks/useGoogleAuth';
import { useLoading } from '@/providers/LoadingProvider';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

type FormInputs = {
  email: string;
  password: string;
  invitation: string | null;
};

export const SignIn = () => {
  const [searchParams] = useSearchParams();
  const invitation = searchParams.get('invitation');
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { idToken, signUpWithGoogle } = useGoogleAuth();
  const { mutate, data, isPending, error } = useSignIn();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({ mode: 'onBlur', criteriaMode: 'all', defaultValues: { invitation } });

  const onSubmit: SubmitHandler<FormInputs> = (formData) => {
    setShowLoading(true);
    mutate(formData);
  };

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    // Google認証でid_tokenを取得出来た場合はSignInを実行
    if (idToken) {
      mutate({ id_token: idToken, invitation });
    }
  }, [idToken]);

  useEffect(() => {
    if (data) {
      navigate('/workspaces', { state: { fromLocation: 'sign_in' } });
    }
  }, [data]);

  return (
    <SessionLayout>
      <main className="main-content mt-0">
        <div
          className="page-header align-items-start min-vh-50 pt-5 pb-11 m-3 border-radius-lg"
          style={{ backgroundImage: `url(${bgImg})`, backgroundColor: '#FBF6FF' }}
        >
          <span className="mask bg-gradient-dark opacity-5"></span>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-5 text-center mx-auto">
                <h1 className="text-white mb-2 mt-5">Nodeへようこそ</h1>
                <p className="text-lead text-white">
                  Googleアカウントまたはメールアドレスでログインしてください。
                  <br />
                  アカウントをお持ちでない方は
                  <br />
                  ユーザー登録ボタンより登録をお願いします。
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="row mt-lg-n10 mt-md-n11 mt-n10 justify-content-center">
            <div className="col-xl-4 col-lg-5 col-md-7 mx-auto">
              <div className="card z-index-0">
                <div className="card-header text-center pt-4">
                  <h5>サインイン</h5>
                </div>
                <div className="card-body">
                  <button className="btn btn-outline-secondary w-100 mb-2" onClick={signUpWithGoogle}>
                    <SpriteSvg name={'icon-google'} />
                    Continue with Google
                  </button>
                  <div className="mb-2 position-relative text-center">
                    <p className="text-sm font-weight-bold mb-2 text-secondary text-border d-inline z-index-2 bg-white px-3">
                      または
                    </p>
                  </div>
                  <form role="form" className="text-start" onSubmit={handleSubmit(onSubmit)}>
                    <ApiErrorMessages error={error} />
                    <div className="mb-3">
                      <input
                        type="email"
                        maxLength={100}
                        className={`form-control ${errors.email && 'is-invalid'}`}
                        placeholder={'メールアドレス'}
                        {...register('email', {
                          required: '入力して下さい。',
                        })}
                      />
                      {errors.email && <div className="text-xs text-danger mt-1">{errors.email.message}</div>}
                    </div>
                    <div className="mb-3">
                      <input
                        type="password"
                        maxLength={50}
                        className={`form-control ${errors.password && 'is-invalid'}`}
                        placeholder={'パスワード'}
                        {...register('password', {
                          required: '入力して下さい。',
                        })}
                      />
                      {errors.password && <div className="text-xs text-danger mt-1">{errors.password.message}</div>}
                    </div>
                    <div className="text-center mb-2">
                      <button type="submit" className="btn bg-gradient-info w-100 mb-2">
                        サインイン
                      </button>
                      <span className="text-muted text-sm">
                        パスワードを忘れてしまった方は<Link to={`/password_reset/new`} className='text-decoration-underline'>こちら</Link>
                      </span>
                    </div>
                    <div className="mb-2 position-relative text-center">
                      <p className="text-sm font-weight-bold mb-2 text-secondary text-border d-inline z-index-2 bg-white px-3">
                        または
                      </p>
                    </div>
                    <div className="text-center mb-3">
                      <Link
                        to={`/sign_up${invitation ? `?invitation=${invitation}` : ''}`}
                        type="button"
                        className="btn bg-gradient-dark w-100 mt-2"
                      >
                        ユーザー登録
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </SessionLayout>
  );
};
